import { CloudCustomerAddress } from "@omni/types/api/customer/cloud/address.model";
import { AddressType } from "@omni/types/api/customer/cloud/shared/enums/addressType.enum";
import { AddressValidationStatus } from "@omni/types/api/customer/cloud/shared/enums/addressValidationStatus.enum";
import { cn } from "@omni/utils/styles";
import { useSelector } from "react-redux";

import { useAddressValidationContext } from "@/components/Address/AddressValidation/context/addressValidation.context";
import { Messages } from "@/components/General/Messages/messages.container";
import { Modal } from "@/components/General/Modal/modal";
import * as AddressSelectors from "@/redux/modules/api/data/address/address.selectors";
import {
    broadcastMessage,
    clearMessageById
} from "@/redux/modules/value/messages/messages.utils";
import { ModalType } from "@/redux/modules/value/modal/modal.types";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { getEmptyAddress } from "@/utils/address/address";
import { BPOSTInvalidAddressMessageId } from "@/utils/address/validation.config";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";

import { EditDefaultAddressForm } from "./components/editDefaultAddressForm.component";
import { EditDeliveryAddressForm } from "./components/editDeliveryAddressForm.component";
import { EditPaymentAddressForm } from "./components/editPaymentAddressForm.component";

type EditAddressModalProps = {
    open: boolean;
    formId?: string;
    title?: string;
    subTitle?: string;
    skipValidation?: boolean;
    handleClosing?: boolean;
    currentAddress: CloudCustomerAddress;
    className?: string;
    setOpen: (isOpen: boolean) => void;
    updateAddress: (address: CloudCustomerAddress) => Promise<void>;
};

const FORM_ID = "editAddressModal";

const EditAddressModal = ({
    open,
    formId,
    title,
    subTitle,
    handleClosing = true,
    skipValidation = false,
    className,
    setOpen,
    currentAddress,
    updateAddress
}: EditAddressModalProps) => {
    const { t } = useGranite();
    const { validate, isValidating } = useAddressValidationContext();
    const isLoadingAddress = useSelector(AddressSelectors.isLoading);

    const determineType = currentAddress.defaultDeliveryAddress
        ? AddressType.Delivery
        : currentAddress.defaultPaymentAddress
          ? AddressType.Payment
          : null;

    const addressType = determineType || AddressType.Default;

    const emptyAddress = getEmptyAddress(addressType);

    const addressToEdit = currentAddress || emptyAddress;

    const onSubmit = async (
        address: CloudCustomerAddress,
        isBPostField: boolean
    ) => {
        clearMessageById(BPOSTInvalidAddressMessageId);

        const validationResult = await validate({
            id: address.id,
            firstName: address.firstName,
            lastName: address.lastName,
            street: address.street,
            number: address.number,
            zipCode: address.zipCode,
            city: address.city,
            country: address.country,
            box: address.box,
            addressing: address.addressing,
            type: addressType,
            isBpostField: isBPostField,
            currentStatus: AddressValidationStatus.Unknown
        });

        if (validationResult === AddressValidationStatus.Invalid) {
            broadcastMessage({
                id: BPOSTInvalidAddressMessageId,
                text: t("bpost.validation.address.invalid"),
                status: "warning",
                dismissible: true
            });
            return;
        }

        if (validationResult === AddressValidationStatus.Closed) {
            return;
        }

        if (validationResult) {
            await updateAddress({
                ...address,
                validationStatus: validationResult
            });
        }

        if (handleClosing) {
            setOpen(false);
        }
    };

    return (
        <Modal
            modalType={ModalType.EditAddress}
            title={
                title ||
                t(
                    "general.address.modal.title." +
                        (determineType || "other").toLowerCase()
                )
            }
            subtitle={
                subTitle ||
                t(
                    "general.address.modal.subtitle." +
                        (determineType || "other").toLowerCase()
                )
            }
            formId={formId || FORM_ID}
            size={"lg"}
            open={open}
            setOpen={setOpen}
            className={cn(className)}
            isLoading={isValidating || isLoadingAddress}
            confirmButtonText={t("general.address.modal.button.label")}
        >
            <Messages messageid={BPOSTInvalidAddressMessageId} />
            <FormInfoProvider
                formId={formId || FORM_ID}
                withNeverbounce={false}
            >
                {() => (
                    <>
                        {addressType === AddressType.Default && (
                            <EditDefaultAddressForm
                                skipValidation={skipValidation}
                                address={addressToEdit}
                                formId={formId || FORM_ID}
                                onFormSubmit={onSubmit}
                            />
                        )}
                        {addressType === AddressType.Payment && (
                            <EditPaymentAddressForm
                                skipValidation={skipValidation}
                                address={addressToEdit}
                                formId={formId || FORM_ID}
                                onFormSubmit={onSubmit}
                            />
                        )}
                        {addressType === AddressType.Delivery && (
                            <EditDeliveryAddressForm
                                skipValidation={skipValidation}
                                address={addressToEdit}
                                formId={formId || FORM_ID}
                                onFormSubmit={onSubmit}
                            />
                        )}
                    </>
                )}
            </FormInfoProvider>
        </Modal>
    );
};

export { EditAddressModal };
